import { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { MessageSquare } from "lucide-react";
import { ORBIT_CHAT_API_BASE, APP_VERSION } from "../config/config";
import { STORAGE_KEYS } from "../constants/storage";
import { getSessionId, generateRequestId } from "../utils/sessionManager";
import "./AskOrbit.css";

const PURIFY_CFG = {
  ALLOWED_TAGS: [
    "h2",
    "h3",
    "p",
    "hr",
    "br",
    "ul",
    "ol",
    "li",
    "strong",
    "em",
    "code",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "div",
    "span"
  ],
  ALLOWED_ATTR: ["class"],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
  FORBID_ATTR: ["onclick", "onerror", "onload", "style", "href", "src"],
  KEEP_CONTENT: true,
  ALLOW_DATA_ATTR: false
};

const SUGGESTED = [
  {
    label: "Forecast at lat/lon",
    q: "What's the weather forecast at latitude 45.5, longitude 170.4?"
  },
  {
    label: "Decode a VIN",
    q: "Decode VIN 1HGCM82633A123456 for model year 2003 using NHTSA."
  },
  {
    label: "Recent FL fires",
    q: "Find federally declared fire disasters in Florida from the last 90 days."
  },
  {
    label: "Multi-step truck valuation",
    q: "Decode commercial truck VIN 3BP1HM6X5DF590051 via Price Digests, get its specs, and look up its market value in Texas with 200,000 miles in Good condition."
  }
];

function getChatBase() {
  if (typeof window !== "undefined" && window.ORBIT_BACKEND_URL) {
    return String(window.ORBIT_BACKEND_URL).replace(/\/$/, "");
  }
  return ORBIT_CHAT_API_BASE;
}

function buildChatHeaders() {
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Session-Id": getSessionId(),
    "X-Request-ID": generateRequestId(),
    "X-Client-Version": APP_VERSION
  };
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const userDetails = localStorage.getItem(STORAGE_KEYS.USER_DETAILS);
  if (userDetails) {
    try {
      const parsed = JSON.parse(userDetails);
      if (parsed.organization_id) {
        headers["X-Organization-ID"] = parsed.organization_id;
      }
    } catch {
      /* ignore */
    }
  }
  return headers;
}

function activityLabel(turn) {
  const { counts, done } = turn;
  const call = counts.call || 0;
  const err = counts.error || 0;
  const total = call;
  if (err > 0) {
    return done
      ? `Completed with ${err} error${err > 1 ? "s" : ""}`
      : "Working…";
  }
  if (done) {
    return total === 0
      ? "No tool calls"
      : total === 1
        ? "Used 1 tool"
        : `Used ${total} tools`;
  }
  return total === 0 ? "Thinking…" : "Working…";
}

async function streamChat(backendBase, message, history, onEvent, signal) {
  const res = await fetch(`${backendBase}/chat`, {
    method: "POST",
    headers: buildChatHeaders(),
    body: JSON.stringify({ message, history }),
    signal
  });
  if (!res.ok || !res.body) {
    throw new Error(`HTTP ${res.status} from backend`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:"))
          data += (data ? "\n" : "") + line.slice(5).trim();
      }
      let parsed = data;
      try {
        parsed = JSON.parse(data);
      } catch {
        /* leave as string */
      }
      onEvent(event, parsed);
    }
  }
}

// AG-UI lives at the backend root (/ag-ui/), not under /api/v1.
function getAgUiUrl() {
  const root = getChatBase().replace(/\/api\/v1\/?$/, "");
  return `${root}/ag-ui/`;
}

// Consume the AG-UI event stream and translate its protocol events into the
// SAME onEvent shapes streamChat emits, so the UI renders identically. The only
// difference is the wire protocol (open AG-UI standard vs Orbit's bespoke SSE).
async function streamAGUI(_backendBase, message, history, onEvent, signal, threadId) {
  // ag_ui_adk stores conversation history in a server-side session keyed by
  // threadId (NOT from this messages array). So we send a STABLE threadId for the
  // whole conversation and only the new user message; the session carries prior
  // turns. A new threadId per message makes the agent forget everything.
  const messages = [{ id: `u-${Date.now()}`, role: "user", content: message }];

  const body = {
    threadId: threadId || `t-${Date.now()}`,
    runId: `r-${Date.now()}`,
    state: {},
    messages,
    tools: [],
    context: [],
    forwardedProps: {}
  };

  const res = await fetch(getAgUiUrl(), {
    method: "POST",
    headers: buildChatHeaders(),
    body: JSON.stringify(body),
    signal
  });
  if (!res.ok || !res.body) {
    throw new Error(`HTTP ${res.status} from AG-UI endpoint`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const toolNames = {}; // toolCallId -> tool name (for TOOL_CALL_RESULT)

  const handle = (evt) => {
    const type = evt && evt.type;
    if (!type) return;
    if (type === "RUN_STARTED") {
      onEvent("status", { text: "Agent run started" });
    } else if (type === "TOOL_CALL_START") {
      const name = evt.toolCallName || "tool";
      if (evt.toolCallId) toolNames[evt.toolCallId] = name;
      onEvent("tool_call", { name, args: {} });
    } else if (type === "TOOL_CALL_RESULT") {
      const name = toolNames[evt.toolCallId] || "tool";
      let result = evt.content;
      try {
        result = JSON.parse(evt.content);
      } catch {
        /* keep raw string */
      }
      onEvent("tool_result", { name, result });
    } else if (type === "TEXT_MESSAGE_CONTENT") {
      if (evt.delta) onEvent("text", { chunk: evt.delta });
    } else if (type === "RUN_ERROR" || type === "ERROR") {
      onEvent("error", { message: evt.message || "agent run error" });
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("data:")) {
          data += (data ? "\n" : "") + line.slice(5).trim();
        }
      }
      if (!data) continue;
      try {
        handle(JSON.parse(data));
      } catch {
        /* skip non-JSON keepalive frames */
      }
    }
  }
}

const ENGINE_KEY = "orbit_ask_engine";

export default function AskOrbit() {
  const historyRef = useRef([]);
  // ag_ui_adk keeps conversation history in a server-side session keyed by
  // threadId, so we use ONE stable threadId for the whole conversation (a new
  // one per message would make the agent forget every prior turn).
  const threadIdRef = useRef(`t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [inFlight, setInFlight] = useState(false);
  const textareaRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const abortRef = useRef(null); // in-flight stream's AbortController

  const [health, setHealth] = useState({
    dot: "",
    text: "Checking backend…"
  });

  // Which transport runs the agent: "chat" (Orbit's bespoke SSE, default) or
  // "agui" (the open AG-UI event protocol). Persisted so a reload keeps it.
  const [engine, setEngine] = useState(() => {
    try {
      return localStorage.getItem(ENGINE_KEY) === "agui" ? "agui" : "chat";
    } catch {
      return "chat";
    }
  });
  const setEnginePersist = useCallback((next) => {
    setEngine(next);
    try {
      localStorage.setItem(ENGINE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollAreaRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [turns, scrollToBottom]);

  useEffect(() => {
    let cancelled = false;
    const base = getChatBase();
    (async () => {
      try {
        const r = await fetch(`${base}/healthz`);
        const j = await r.json();
        if (cancelled) return;
        if (j.ok && j.mcp_url_set && j.token_set) {
          setHealth({
            dot: "ok",
            text: `Connected · ${j.model || "gemini"}`
          });
        } else {
          setHealth({ dot: "bad", text: "Backend misconfigured" });
        }
      } catch {
        if (!cancelled) {
          setHealth({ dot: "bad", text: "Backend unreachable" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Abort any in-flight chat stream on unmount so a navigated-away run doesn't
  // keep reading the response body / updating an unmounted component.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [input, autoResize]);

  const pushModelRow = useCallback((turnId, updateRow) => {
    setTurns((prev) =>
      prev.map((t) => {
        if (t.role !== "model" || t.id !== turnId) return t;
        return updateRow(t);
      })
    );
  }, []);

  const send = async (rawText) => {
    const text = rawText.trim();
    if (inFlight || !text) return;

    setInFlight(true);
    setInput("");

    const userText = text;
    setTurns((prev) => [...prev, { role: "user", id: `u-${Date.now()}`, text: userText }]);
    historyRef.current.push({ role: "user", text: userText });

    const turnId = `m-${Date.now()}`;
    const modelTurn = {
      role: "model",
      id: turnId,
      html: "",
      typing: true,
      done: false,
      activityVisible: false,
      activityOpen: false,
      rows: [],
      counts: { call: 0, result: 0, error: 0, status: 0 }
    };
    setTurns((prev) => [...prev, modelTurn]);

    const backend = getChatBase();
    const streamFn = engine === "agui" ? streamAGUI : streamChat;
    let finalHtml = "";

    // Track this stream so unmount cancels it; abort any prior one first.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamFn(backend, userText, historyRef.current, (event, data) => {
        if (event === "status") {
          pushModelRow(turnId, (t) => {
            const c = { ...t.counts, status: t.counts.status + 1 };
            return {
              ...t,
              activityVisible: true,
              counts: c,
              rows: [
                ...t.rows,
                { kind: "status", name: null, payload: data?.text || "" }
              ]
            };
          });
        } else if (event === "tools") {
          pushModelRow(turnId, (t) => ({
            ...t,
            activityVisible: true,
            counts: { ...t.counts, status: t.counts.status + 1 },
            rows: [
              ...t.rows,
              {
                kind: "status",
                name: "tools available",
                payload: (data?.names || []).join(", ")
              }
            ]
          }));
        } else if (event === "tool_call") {
          pushModelRow(turnId, (t) => {
            const c = { ...t.counts, call: t.counts.call + 1 };
            return {
              ...t,
              activityVisible: true,
              counts: c,
              rows: [
                ...t.rows,
                { kind: "call", name: data?.name, payload: data?.args }
              ]
            };
          });
        } else if (event === "tool_result") {
          pushModelRow(turnId, (t) => {
            const c = { ...t.counts, result: t.counts.result + 1 };
            return {
              ...t,
              activityVisible: true,
              counts: c,
              rows: [
                ...t.rows,
                { kind: "result", name: data?.name, payload: data?.result }
              ]
            };
          });
        } else if (event === "text") {
          finalHtml += data?.chunk || "";
          pushModelRow(turnId, (t) => ({
            ...t,
            html: finalHtml,
            typing: true
          }));
        } else if (event === "error") {
          pushModelRow(turnId, (t) => {
            const c = { ...t.counts, error: t.counts.error + 1 };
            return {
              ...t,
              activityVisible: true,
              counts: c,
              rows: [
                ...t.rows,
                {
                  kind: "error",
                  name: null,
                  payload: data?.message || "unknown error"
                }
              ]
            };
          });
        }
      }, controller.signal, threadIdRef.current);

      pushModelRow(turnId, (t) => ({
        ...t,
        typing: false,
        done: true,
        html: finalHtml
      }));

      if (!finalHtml.trim()) {
        pushModelRow(turnId, (t) => ({ ...t, html: "", emptyResponse: true }));
      } else {
        historyRef.current.push({ role: "model", text: finalHtml });
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // cancelled, not an error
      const msg = String(e.message || e);
      pushModelRow(turnId, (t) => {
        const c = { ...t.counts, error: t.counts.error + 1 };
        return {
          ...t,
          typing: false,
          done: true,
          html: "",
          failed: true,
          counts: c,
          activityVisible: true,
          rows: [
            ...t.rows,
            { kind: "error", name: null, payload: msg }
          ]
        };
      });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setInFlight(false);
    }
  };

  const onKeyDown = (e) => {
    const submit =
      (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ||
      (e.key === "Enter" && !e.shiftKey && !e.isComposing);
    if (submit) {
      e.preventDefault();
      send(input);
    }
  };

  const showEmpty = turns.length === 0;

  return (
    <div className="ask-orbit">
      <div className="ask-orbit-toprow">
        <span className="ask-orbit-health">
          <span className={`dot ${health.dot}`} />
          <span>{health.text}</span>
          {engine === "agui" && (
            <span className="ask-orbit-engine-badge">via AG-UI</span>
          )}
        </span>
        <span
          className="ask-orbit-engine"
          role="group"
          aria-label="Agent engine"
          title="Transport that runs the agent. Standard = Orbit's SSE. AG-UI = the open Agent-User Interaction protocol (same agent, standard wire format)."
        >
          <span className="ask-orbit-engine-label">Engine</span>
          <button
            type="button"
            className={`ask-orbit-engine-pill ${engine === "chat" ? "active" : ""}`}
            onClick={() => setEnginePersist("chat")}
            disabled={inFlight}
          >
            Standard
          </button>
          <button
            type="button"
            className={`ask-orbit-engine-pill ${engine === "agui" ? "active" : ""}`}
            onClick={() => setEnginePersist("agui")}
            disabled={inFlight}
          >
            AG-UI
          </button>
        </span>
      </div>

      <div className="ask-orbit-main">
        <div ref={scrollAreaRef} className="scroll-area">
          <div className="content">
            <div className="suggested-section">
              <div className="suggested-label">Try a question</div>
              <div className="suggested">
                {SUGGESTED.map((item) => (
                  <button
                    key={item.q}
                    type="button"
                    onClick={() => send(item.q)}
                    disabled={inFlight}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="conversation">
              {showEmpty && (
                <div className="empty">
                  <div className="empty-icon">
                    <MessageSquare
                      width={22}
                      height={22}
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                  <h2>Start a conversation</h2>
                  <p>
                    Pick a suggestion above or type a question below.
                    <br />
                    Orbit will pick the right endpoints and chain calls as needed.
                  </p>
                </div>
              )}

              {turns.map((turn) => {
                if (turn.role === "user") {
                  return (
                    <div key={turn.id} className="turn user">
                      <div className="role">
                        <span className="role-avatar">Y</span>
                        <span>You</span>
                      </div>
                      <div className="body">{turn.text}</div>
                    </div>
                  );
                }

                const label = activityLabel(turn);
                const callTotal = turn.counts.call || 0;
                const showPill = callTotal > 0;

                return (
                  <div key={turn.id} className="turn model">
                    <div className="role">
                      <span className="role-avatar">O</span>
                      <span>Orbit</span>
                    </div>

                    {turn.activityVisible && (
                      <div
                        className={`activity-wrap${turn.activityOpen ? " open" : ""}`}
                      >
                        <button
                          type="button"
                          className="activity-toggle"
                          onClick={() =>
                            setTurns((prev) =>
                              prev.map((t) =>
                                t.id === turn.id
                                  ? { ...t, activityOpen: !t.activityOpen }
                                  : t
                              )
                            )
                          }
                        >
                          {!turn.done && (
                            <span className="spinner" aria-hidden />
                          )}
                          <svg
                            className="chev"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                          <span>{label}</span>
                          {showPill && (
                            <span className="count-pill">{callTotal}</span>
                          )}
                        </button>
                        <div className="activity">
                          {turn.rows.map((row, i) => (
                            <div key={i} className="row">
                              <div className="label">
                                <span className={`pill ${row.kind}`}>
                                  {row.kind}
                                </span>
                                {row.name != null && row.name !== "" && (
                                  <span className="name">{row.name}</span>
                                )}
                              </div>
                              {row.payload !== undefined &&
                                row.payload !== null &&
                                row.payload !== "" && (
                                  <details className="json">
                                    <summary>details</summary>
                                    <pre className="code">
                                      {typeof row.payload === "string"
                                        ? row.payload
                                        : JSON.stringify(row.payload, null, 2)}
                                    </pre>
                                  </details>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {turn.failed && (
                      <div className="body">Request failed.</div>
                    )}
                    {!turn.failed && turn.emptyResponse && (
                      <div className="body">(no response)</div>
                    )}
                    {!turn.failed && !turn.emptyResponse && (
                      <div
                        className={`body report${
                          turn.typing ? " typing-cursor" : ""
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(turn.html || "", PURIFY_CFG)
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="composer-wrap">
          <div className="composer">
            <div className="composer-inner">
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Ask Orbit anything — VIN, weather, disaster, valuation…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={inFlight}
              />
              <button
                type="button"
                className="send"
                disabled={inFlight}
                onClick={() => send(input)}
              >
                <span>Send</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
            <div className="hint">
              <kbd>⌘</kbd>/<kbd>Ctrl</kbd> + <kbd>Enter</kbd> or{" "}
              <kbd>Enter</kbd> to send · <kbd>Shift</kbd> + <kbd>Enter</kbd>{" "}
              for newline
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
